import queryString from 'query-string'
import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { ChangeFiltersType } from '@shared/types'

interface UseFiltersOptions {
  groupKey?: string;
}

export const useFilters = <Type>({ groupKey }: UseFiltersOptions = {}) => {
  const [ searchParams ] = useSearchParams()
  const navigate = useNavigate()

  const changeFilters = (changedFilters: ChangeFiltersType) => {
    const allParams = Object.fromEntries(searchParams.entries())

    if (groupKey) {
      const updatedGroupFilters = changedFilters

      const cleanedGroupFilters = Object.fromEntries(
        Object.entries(updatedGroupFilters).filter(
          ([ _, value ]) => value !== undefined && value !== null && value !== '',
        ),
      )

      if (Object.keys(cleanedGroupFilters).length > 0) {
        const groupFiltersString = queryString.stringify(cleanedGroupFilters, {
          arrayFormat: 'bracket',
        })

        const newQueryString = queryString.stringify({
          ...allParams,
          [groupKey]: groupFiltersString,
        })

        navigate(`?${newQueryString}`, { replace: true })
      }
      else {
        resetFilters()
      }
    }
    else {
      const currentFilters = queryString.parse(searchParams.toString(), {
        arrayFormat: 'bracket',
      })

      const updatedFilters = {
        ...currentFilters,
        ...changedFilters,
      }

      const cleanedFilters = Object.fromEntries(
        Object.entries(updatedFilters).filter(
          ([ _, value ]) => value !== undefined && value !== null && value !== '',
        ),
      )

      const newQueryString = queryString.stringify(cleanedFilters, {
        arrayFormat: 'bracket',
      })

      navigate(`?${newQueryString}`, { replace: true })
    }
  }

  const resetFilters = () => {
    const allParams = Object.fromEntries(searchParams.entries())

    if (groupKey) {
      const { [groupKey]: _, ...rest } = allParams

      const newQueryString = queryString.stringify(rest, {
        arrayFormat: 'bracket',
      })

      navigate(`?${newQueryString}`, { replace: true })
    }
    else {
      navigate('?', { replace: true })
    }
  }

  const filters = useMemo(() => {
    if (groupKey) {
      const groupFiltersString = searchParams.get(groupKey) || ''
      return queryString.parse(groupFiltersString, {
        arrayFormat: 'bracket',
      }) as Type
    }
    else {
      return queryString.parse(searchParams.toString(), {
        arrayFormat: 'bracket',
      }) as Type
    }
  }, [ searchParams, groupKey ])

  return { filters, changeFilters, resetFilters }
}
